import React, { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Content,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Tab,
  TabTitleText,
  Tabs,
} from '@patternfly/react-core';
import { FileContractIcon } from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import MicrosoftSplaTab from './MicrosoftSplaTab';
import Microsoft365Tab from './Microsoft365Tab';

type LicensesTabKey = 'spla' | 'microsoft-365';

/**
 * License & Subscriptions — software licensing bound to the tenant's infrastructure.
 * Microsoft SPLA shows the organization's current license consumption (GET /clients/spla);
 * Microsoft 365 stays a placeholder until its backend exists. Tabs mount lazily (mountOnEnter),
 * so each tab's fetch fires on first open only.
 */
function Licenses() {
  const auth = useAuth();
  const [activeTabKey, setActiveTabKey] = useState<LicensesTabKey>('spla');

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
          <BreadcrumbItem isActive>License &amp; Subscriptions</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Content component="h1">
          <Icon size="lg" style={{ marginRight: '8px' }}>
            <FileContractIcon />
          </Icon>
          License &amp; Subscriptions
        </Content>
        <Content component="p">Manage your software licenses and subscriptions</Content>
      </PageSection>
      <PageSection hasBodyWrapper={false}>
        <Tabs
          activeKey={activeTabKey}
          onSelect={(_event, tabKey) => setActiveTabKey(tabKey as LicensesTabKey)}
          mountOnEnter
          aria-label="License and subscription sections"
        >
          <Tab eventKey="spla" title={<TabTitleText>Microsoft SPLA</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <MicrosoftSplaTab />
            </div>
          </Tab>
          <Tab eventKey="microsoft-365" title={<TabTitleText>Microsoft 365</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <Microsoft365Tab />
            </div>
          </Tab>
        </Tabs>
      </PageSection>
    </>
  );
}

export { Licenses };
